import { useEffect, useMemo } from 'react';

import { FieldValues, UseFormReturn } from 'react-hook-form';

import { areValuesEqual, compareFormValues } from '@/utils/form-state.utils';

import { FeatureMode } from '../types/types';
import { BaseTabConfig, BaseTabProps, useBaseTab } from './use-base-tab';

/**
 * Configuration for tab form state management
 */
export interface TabFormStateConfig<TData, TFormData extends FieldValues = FieldValues>
  extends BaseTabConfig<TData> {
  /**
   * The React Hook Form instance
   */
  form: UseFormReturn<TFormData>;

  /**
   * Initial values for comparison in edit mode
   */
  initialValues?: TFormData;

  /**
   * Custom function to calculate if form has changes
   */
  customDirtyStateFn?: (currentValues: TFormData, initialValues?: TFormData) => boolean;

  /**
   * Whether to trigger validation when component mounts with draft data
   */
  triggerValidationOnMount?: boolean;

  /**
   * List of field names that are "non-versioned" (don't require conflict detection)
   * When only these fields changed, the date picker will be hidden
   * Example: ['name', 'color', 'clientId', 'description']
   */
  nonVersionedFields?: (keyof TFormData)[];
}

/**
 * Return interface for tab form state hook
 */
export interface TabFormStateReturn<TFormData extends FieldValues = FieldValues> {
  /** Whether the entity is archived */
  isArchivedEntity: boolean;

  /** Whether the tab is in read-only mode */
  isReadMode: boolean;

  /** Function to notify parent of form state changes */
  notifyFormStateChange: (
    isDirty: boolean,
    hasChanges?: boolean,
    isValid?: boolean,
    hasOnlyNonVersionedChanges?: boolean
  ) => void;

  /** Current form values */
  currentValues: TFormData;

  /** Function to calculate if form is dirty */
  calculateDirtyState: () => boolean;

  /** Whether only non-versioned fields have changed (computed value) */
  hasOnlyNonVersionedChanges: boolean;

  /** Function to handle form submission with validation */
  handleSubmit: () => Promise<void>;

  /** Function to get form data for parent */
  getFormData: () => TFormData;
}

/**
 * Hook for managing individual tab form state.
 * Provides form validation, dirty state tracking, and data access for single tabs.
 *
 * @example
 * ```typescript
 * function MyFormTab({ mode, projectData, draftData, onFormStateChange }: MyTabProps) {
 *   const form = useForm<MyFormData>({
 *     resolver: zodResolver(schema),
 *     defaultValues: initialValues
 *   });
 *
 *   const { isReadMode, currentValues, handleSubmit, getFormData } = useTabFormState({
 *     mode,
 *     projectData,
 *     draftData,
 *     onFormStateChange
 *   }, {
 *     form,
 *     initialValues,
 *     isArchivedFn: (data) => data?.status === 'archived'
 *   });
 *
 *   // Your component JSX
 * }
 * ```
 */
export function useTabFormState<TData, TFormData extends FieldValues = FieldValues>(
  props: BaseTabProps<TData, TFormData>,
  config: TabFormStateConfig<TData, TFormData>
): TabFormStateReturn<TFormData> {
  const { mode, draftData } = props;
  const {
    form,
    initialValues,
    customDirtyStateFn,
    triggerValidationOnMount = false,
    nonVersionedFields = [],
    ...baseConfig
  } = config;

  // Get current form values
  const currentValues = form.watch();

  /**
   * Calculate if the form has changes (for form validation/save button)
   */
  const calculateDirtyState = useMemo(() => {
    return () => {
      if (!currentValues) return false;

      // Use custom dirty state function if provided
      if (customDirtyStateFn) {
        return customDirtyStateFn(currentValues, initialValues);
      }

      // Default behavior based on mode
      if (mode === FeatureMode.Create) {
        // For create mode, check if form is valid and has required data
        return Boolean(
          form.formState.isValid &&
            Object.keys(currentValues).some(key => {
              const value = currentValues[key as keyof TFormData];
              return value !== undefined && value !== '' && value !== null;
            })
        );
      }

      // For edit mode, compare with initial values
      if (initialValues) {
        return compareFormValues(currentValues, initialValues);
      }

      // Fallback to form's built-in dirty state
      return form.formState.isDirty;
    };
  }, [
    currentValues,
    initialValues,
    customDirtyStateFn,
    form.formState.isValid,
    form.formState.isDirty,
    mode,
  ]);

  /**
   * Calculate pure changes state (for confirmation dialogs)
   */
  const calculateHasChanges = useMemo(() => {
    return () => {
      if (!currentValues) return false;

      // For create mode, compare with initial values to detect user modifications
      // Only compare keys from initialValues to ignore extra fields registered by the form
      // (e.g. avatar/logo fields added by the template but not in the tab's initial values)
      if (mode === FeatureMode.Create) {
        if (initialValues) {
          const initialKeys = Object.keys(initialValues);
          const filteredCurrent: Record<string, unknown> = {};
          for (const key of initialKeys) {
            filteredCurrent[key] = currentValues[key as keyof TFormData];
          }
          return compareFormValues(filteredCurrent, initialValues);
        }
        return form.formState.isDirty;
      }

      // For edit mode, compare with initial values
      if (initialValues) {
        if (customDirtyStateFn) {
          return customDirtyStateFn(currentValues, initialValues);
        } else {
          return compareFormValues(currentValues, initialValues);
        }
      }

      // Fallback to form's built-in dirty state
      return form.formState.isDirty;
    };
  }, [currentValues, initialValues, form.formState.isDirty, mode]);

  /**
   * Calculate form validity state
   */
  const calculateIsValid = useMemo(() => {
    return () => form.formState.isValid;
  }, [form.formState.isValid]);

  /**
   * Calculate if only non-versioned fields have changed
   * Returns true when: form has changes AND all changed fields are in nonVersionedFields list
   * This is a computed value (not a function) to avoid unnecessary re-renders
   */
  const hasOnlyNonVersionedChanges = useMemo((): boolean => {
    // Only applicable in edit mode with initialValues
    if (mode === FeatureMode.Create || !initialValues || nonVersionedFields.length === 0) {
      return false;
    }

    // Find all fields that have changed
    const changedFields = (Object.keys(currentValues) as (keyof TFormData)[]).filter(key => {
      const currentVal = currentValues[key];
      const initialVal = initialValues[key];
      return !areValuesEqual(currentVal, initialVal);
    });

    // If no fields changed, return false (no changes at all)
    if (changedFields.length === 0) {
      return false;
    }

    // Return true only if ALL changed fields are non-versioned
    return changedFields.every(field => nonVersionedFields.includes(field));
  }, [currentValues, initialValues, nonVersionedFields, mode]);

  // Use base tab functionality with form-aware dirty state calculation
  const baseTab = useBaseTab(props, {
    ...baseConfig,
    calculateDirtyStateFn: calculateDirtyState,
    calculateHasChangesFn: calculateHasChanges,
    calculateIsValidFn: calculateIsValid,
    hasOnlyNonVersionedChangesValue: hasOnlyNonVersionedChanges,
  });

  /**
   * Handle form submission with validation
   */
  const handleSubmit = async (): Promise<void> => {
    const isValid = await form.trigger();
    if (!isValid) {
      throw new Error('Form validation failed');
    }
  };

  /**
   * Get form data for parent component
   */
  const getFormData = (): TFormData => {
    return form.getValues();
  };

  /**
   * Trigger validation when component mounts with draft data
   */
  useEffect(() => {
    if (triggerValidationOnMount && mode === FeatureMode.Create && draftData) {
      form.trigger();
    }
  }, [form, mode, draftData, triggerValidationOnMount]);

  return {
    isArchivedEntity: baseTab.isArchivedEntity,
    isReadMode: baseTab.isReadMode,
    notifyFormStateChange: baseTab.notifyFormStateChange,
    currentValues,
    calculateDirtyState,
    hasOnlyNonVersionedChanges,
    handleSubmit,
    getFormData,
  };
}
